/* global debounce */

if (!customElements.get('search-form')) {
  class SearchForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.closest('form');
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.addListeners();
      this.init();
    }

    addListeners() {
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.resetBtn.addEventListener('click', this.handleReset.bind(this));
      if (this.form) this.form.addEventListener('submit', this.handleSubmit.bind(this));
    }

    /**
     * Builds a SKU-field query if the search term looks like one or more SKUs. Shopify's
     * general index misses some full SKUs, but a 'variants.sku:' query always finds them.
     * All store SKUs are 10 digits starting with '0001', so codes typed without the leading
     * zeros (e.g. '1060420' copied from a spreadsheet) are padded with '000'.
     * @param {string} term - Search term.
     * @returns {?string} SKU query, or null if the term isn't SKU-like.
     */
    static getSkuQuery(term) {
      const tokens = term.split(/[\s,;]+/).filter(Boolean);
      if (!tokens.length || !tokens.every((token) => /^\d{5,10}$/.test(token))) return null;

      return tokens
        .map((token) => `variants.sku:${token.length <= 7 && !token.startsWith('0') ? `000${token}` : token}`)
        .join(' OR ');
    }

    /**
     * Handles search form submit. SKU searches go straight to the product when there is a
     * single match, otherwise to the results page using the SKU-field query.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      const skuQuery = SearchForm.getSkuQuery(this.input.value.trim());
      if (
        !skuQuery
        || evt.defaultPrevented
        || this.form.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
      ) return;

      evt.preventDefault();

      try {
        const params = new URLSearchParams({
          q: skuQuery,
          'resources[type]': 'product',
          'resources[limit]': 2,
          'resources[options][fields]': 'variants.sku'
        });
        const response = await fetch(`${theme.routes.predictiveSearch}.json?${params}`);
        if (!response.ok) throw new Error(response.status);

        const { products } = (await response.json()).resources.results;
        if (products.length === 1) {
          window.location.href = products[0].url;
          return;
        }

        if (products.length > 1) {
          window.location.href = `${this.form.action}?${new URLSearchParams({ q: skuQuery, type: 'product' })}`;
          return;
        }
      } catch (error) {
        // Fall back to a normal search below.
      }

      this.form.submit();
    }

    init() {
      if ((this.input.dataset.placeholderTwo || this.input.dataset.placeholderThree)
        && (this.input.dataset.placeholderPromptsMob === 'true' || theme.mediaMatches.md)) {
        this.typeInPlaceholders();
      }
    }

    /**
     * Simulates the placeholder titles being typed in and the previous one deleted, on repeat
     */
    typeInPlaceholders() {
      const typingSpeed = this.input.dataset.typingSpeed || 100;
      const deletingSpeed = this.input.dataset.deletingSpeed || 60;
      const delayAfterDeleting = this.input.dataset.delayAfterDeleting || 500;
      const delayBeforeFirstDelete = this.input.dataset.delayBeforeFirstDelete || 2000;
      const delayAfterWordTyped = this.input.dataset.delayAfterWordTyped || 2400;
      const placeholders = [];

      if (this.input.placeholder) {
        placeholders.push(this.input.placeholder);
      }

      if (this.input.dataset.placeholderTwo) {
        placeholders.push(this.input.dataset.placeholderTwo);
      }

      if (this.input.dataset.placeholderThree) {
        placeholders.push(this.input.dataset.placeholderThree);
      }

      const typeInNextPlaceholder = async (placeholder) => {
        await new Promise((resolve) => {
          let currentPlaceholder = this.input.getAttribute('placeholder');

          // If necessary remove the start of the word to type in if it already exists in the
          // placeholder
          let nextPlaceholder = (currentPlaceholder.length >= 3
              && placeholder.startsWith(currentPlaceholder))
            ? placeholder.replace(currentPlaceholder, '')
            : placeholder;

          const typingIntervalId = setInterval(() => {
            currentPlaceholder = this.input.getAttribute('placeholder');
            this.input.setAttribute('placeholder', currentPlaceholder + nextPlaceholder.charAt(0));
            if (nextPlaceholder.length === 1) {
              resolve();
              clearInterval(typingIntervalId); // Stop typing
            } else {
              nextPlaceholder = nextPlaceholder.substring(1);
            }
          }, typingSpeed);
        });
      };

      const deleteCurrentPlaceholder = async (nextPlaceholder) => {
        await new Promise((resolve) => {
          let prevPlaceholder = this.input.getAttribute('placeholder');
          const deletionIntervalId = setInterval(() => {
            const newPlaceholder = prevPlaceholder.substring(0, prevPlaceholder.length - 1);
            this.input.setAttribute('placeholder', newPlaceholder);
            prevPlaceholder = newPlaceholder;
            if (prevPlaceholder.length === 0
              || (prevPlaceholder.length >= 3 && nextPlaceholder.startsWith(prevPlaceholder))) {
              resolve();
              clearInterval(deletionIntervalId);
            }
          }, deletingSpeed);
        });
      };

      let startIndex = 0;
      const showNextPlaceholder = () => {
        startIndex = (startIndex + 1) % placeholders.length;
        const nextPlaceholder = placeholders[startIndex];
        deleteCurrentPlaceholder.call(this, nextPlaceholder).then(() => {
          setTimeout(() => {
            typeInNextPlaceholder.call(this, nextPlaceholder).then(() => {
              setTimeout(showNextPlaceholder.bind(this), delayAfterWordTyped);
            });
          }, delayAfterDeleting);
        });
      };

      setTimeout(showNextPlaceholder.bind(this), delayBeforeFirstDelete);
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      if (this.input.value.length > 0) {
        this.input.classList.add('search__input--dirty');
      } else {
        this.input.classList.remove('search__input--dirty');
      }
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
    }

    /**
     * Handles the reset button being clicked
     */
    handleReset() {
      this.input.classList.remove('search__input--dirty');
    }
  }

  customElements.define('search-form', SearchForm);
}
